import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Bot,
  Building2,
  ChevronsLeft,
  ChevronsRight,
  Settings,
  X,
  Zap,
} from 'lucide-react'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { sidebarUiTokens } from './sidebar-ui-tokens'
import { getWorkspaceSidebarNavigationState } from './workspace-sidebar-navigation'
import type { BootstrapWorkspaceRef } from '@/features/bootstrap/domain/bootstrap-workspaces'
import { AppSidebarShell } from '@/components/molecules/sidebar/app-sidebar-shell'
import { SidebarNavItem } from '@/components/molecules/sidebar/sidebar-nav-item'
import { SidebarTooltip } from '@/components/molecules/sidebar/sidebar-tooltip'
import { IconButton } from '@/components/ui/icon-button'
import { SidebarChatList } from '@/features/chats/components/sidebar-chat-list'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useCurrentLang } from '@/shared/i18n/use-current-lang'
import { useWorkspaceChatSidebarDomain } from '@/features/chats/domain/use-workspace-chat-sidebar-domain'
import { GtmHireButton } from '@/features/gtm/workspace/gtm-hire-button'
import { GtmSidebarUsage } from '@/features/gtm/workspace/gtm-sidebar-usage'
import { GtmWorkspaceNav } from '@/features/gtm/workspace/gtm-workspace-nav'
import { useWorkspaceShellContext } from '@/features/workspaces/domain/use-workspace-shell-context'
import { GTM_PRODUCT_SCOPE } from '@/shared/lib/gtm-product-scope'
import { cn } from '@/shared/lib/utils'

interface WorkspacesSidebarProps {
  mobileOpen?: boolean
  onMobileOpenChange?: (open: boolean) => void
}

const COLLAPSED_STORAGE_KEY = 'laplace:sidebar-collapsed'
const DESKTOP_QUERY = '(min-width: 768px)'

function readCollapsedPreference(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSED_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

function writeCollapsedPreference(collapsed: boolean) {
  try {
    window.localStorage.setItem(COLLAPSED_STORAGE_KEY, collapsed ? '1' : '0')
  } catch {
    // Unavailable storage only loses the preference, not the layout.
  }
}

/** The icon rail exists on desktop only; the mobile drawer is always full. */
function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState(
    () => window.matchMedia(DESKTOP_QUERY).matches,
  )
  useEffect(() => {
    const query = window.matchMedia(DESKTOP_QUERY)
    const handleChange = (event: MediaQueryListEvent) =>
      setIsDesktop(event.matches)
    query.addEventListener('change', handleChange)
    return () => query.removeEventListener('change', handleChange)
  }, [])
  return isDesktop
}

function workspaceInitials(name: string | null | undefined): string {
  return (
    name
      ?.trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part.charAt(0).toLocaleUpperCase())
      .join('') || ''
  )
}

function SidebarDivider() {
  return <div className={cn('my-1 shrink-0', sidebarUiTokens.sectionDivider)} />
}

function WorkspaceSelector({
  collapsed,
  workspaceId,
  workspaces,
  currentWorkspace,
  onSelect,
}: {
  collapsed: boolean
  workspaceId: string
  workspaces: Array<BootstrapWorkspaceRef>
  currentWorkspace: BootstrapWorkspaceRef | null
  onSelect: (workspaceId: string) => void
}) {
  const { t } = useTranslation('workspace')
  const initials = workspaceInitials(currentWorkspace?.name)
  const label = currentWorkspace?.name
    ? `${t('selectWorkspace')}: ${currentWorkspace.name}`
    : t('selectWorkspace')

  const select = (
    <Select
      value={workspaceId.length > 0 ? workspaceId : undefined}
      onValueChange={onSelect}
    >
      <SelectTrigger
        className={cn(
          'bg-card',
          collapsed
            ? 'mx-auto size-9 justify-center gap-0 rounded-md p-0 [&>svg]:hidden'
            : 'h-9 w-full',
        )}
        aria-label={label}
      >
        {collapsed ? (
          <SelectValue>
            {initials ? (
              <span className="text-xs font-semibold">{initials}</span>
            ) : (
              <Building2 className="size-4" />
            )}
          </SelectValue>
        ) : (
          <SelectValue placeholder={t('selectWorkspace')} />
        )}
      </SelectTrigger>
      <SelectContent
        side={collapsed ? 'right' : undefined}
        align="start"
        className="min-w-56"
      >
        {workspaces.map((workspace) => (
          <SelectItem key={workspace.id} value={workspace.id}>
            {workspace.name ?? 'Workspace'}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )

  return collapsed ? (
    <SidebarTooltip label={currentWorkspace?.name ?? t('selectWorkspace')}>
      {select}
    </SidebarTooltip>
  ) : (
    select
  )
}

export function WorkspacesSidebar({
  mobileOpen = false,
  onMobileOpenChange,
}: WorkspacesSidebarProps) {
  const { t } = useTranslation('workspace')
  const { t: tGtm } = useTranslation('gtm')
  const [collapsedPreference, setCollapsedPreference] = useState(
    readCollapsedPreference,
  )
  const isDesktop = useIsDesktop()
  const collapsed = collapsedPreference && isDesktop
  const shell = useWorkspaceShellContext()
  const { pathname } = shell

  useEffect(() => {
    onMobileOpenChange?.(false)
  }, [onMobileOpenChange, pathname])

  const setCollapsed = (next: boolean) => {
    setCollapsedPreference(next)
    writeCollapsedPreference(next)
  }

  const sidebarHeaderAction = !collapsed ? (
    <div className="flex items-center gap-1">
      <IconButton
        icon={<X className={sidebarUiTokens.headerActionIcon} />}
        variant="ghost"
        className={cn(sidebarUiTokens.headerActionButton, 'md:hidden')}
        onClick={() => onMobileOpenChange?.(false)}
        ariaLabel={t('collapseChatSidebar')}
      />
      <IconButton
        icon={<ChevronsLeft className={sidebarUiTokens.headerActionIcon} />}
        variant="ghost"
        className={cn(
          sidebarUiTokens.headerActionButton,
          'hidden md:inline-flex',
        )}
        onClick={() => setCollapsed(true)}
        ariaLabel={t('collapseChatSidebar')}
      />
    </div>
  ) : (
    <SidebarTooltip label={t('expandChatSidebar')}>
      <IconButton
        icon={<ChevronsRight className={sidebarUiTokens.headerActionIcon} />}
        variant="ghost"
        className={cn(
          sidebarUiTokens.headerActionButton,
          'hidden md:inline-flex',
        )}
        onClick={() => setCollapsed(false)}
        ariaLabel={t('expandChatSidebar')}
      />
    </SidebarTooltip>
  )

  return (
    <>
      {mobileOpen ? (
        <button
          type="button"
          className="fixed inset-0 z-30 bg-background/70 backdrop-blur-sm md:hidden"
          aria-label={t('collapseChatSidebar')}
          onClick={() => onMobileOpenChange?.(false)}
        />
      ) : null}
      <AppSidebarShell
        collapsed={collapsed}
        mobileOpen={mobileOpen}
        headerAction={sidebarHeaderAction}
        brand={GTM_PRODUCT_SCOPE.legacyWorkspaceUi ? undefined : tGtm('brand')}
        className={cn(
          'fixed inset-y-0 left-0 z-40 min-h-0 w-[min(20rem,86vw)] shrink-0 border-r bg-sidebar transition-transform duration-200 md:static md:z-auto md:h-full md:w-auto md:border-r md:shadow-none md:transition-none',
          mobileOpen && 'shadow-xl',
        )}
        panelClassName={collapsed ? 'md:w-16' : 'md:w-72'}
        footer={
          !GTM_PRODUCT_SCOPE.legacyWorkspaceUi &&
          shell.workspace?.employees.includes('gtm') ? (
            <GtmSidebarUsage
              workspaceId={shell.workspace.id}
              collapsed={collapsed}
            />
          ) : undefined
        }
      >
        {GTM_PRODUCT_SCOPE.legacyWorkspaceUi ? (
          <LegacyWorkspaceSidebarBody collapsed={collapsed} />
        ) : shell.workspace?.employees.includes('gtm') ? (
          <div
            className={cn(
              'grid shrink-0 gap-1',
              collapsed && 'justify-items-center',
            )}
          >
            <GtmWorkspaceNav
              workspaceId={shell.workspace.id}
              collapsible={false}
              collapsed={collapsed}
            />
          </div>
        ) : null}
      </AppSidebarShell>
    </>
  )
}

/**
 * The team/workspace product's sidebar: workspace switcher, Agents,
 * Automations, workspace Settings and chats. Hidden while Laplace x GTM is a
 * single-user product (see GTM_PRODUCT_SCOPE); kept intact to switch back.
 */
function LegacyWorkspaceSidebarBody({ collapsed }: { collapsed: boolean }) {
  const { t } = useTranslation('workspace')
  const navigate = useNavigate()
  const lang = useCurrentLang()
  const shell = useWorkspaceShellContext()
  const { pathname } = shell
  const routeParams = useParams({ strict: false })
  const routeWorkspaceId =
    'workspaceId' in routeParams && typeof routeParams.workspaceId === 'string'
      ? routeParams.workspaceId
      : ''
  const chatId =
    'chatId' in routeParams && typeof routeParams.chatId === 'string'
      ? routeParams.chatId
      : null

  const {
    workspaceId,
    workspaces,
    chats,
    currentChatId,
    respondingChatIds,
    draftChatIds,
    isChatsLoading,
    isWorkspacesLoading,
    chatsErrorMessage,
    hasWorkspaceAccess,
    handleWorkspaceChange,
    handleOpenChat,
    handleDeleteChat,
  } = useWorkspaceChatSidebarDomain(routeWorkspaceId, chatId)
  const handleNewChat = () => {
    if (!workspaceId) {
      return
    }

    navigate({
      to: '/$lang/workspace/$workspaceId',
      params: { lang, workspaceId },
    })
  }

  const currentWorkspace =
    workspaces.find((workspace) => workspace.id === workspaceId) ?? null
  const { showWorkspaceSelector, isGtmOnlyWorkspace } =
    getWorkspaceSidebarNavigationState({
      isLoading: isWorkspacesLoading,
      workspaces,
      currentWorkspace,
    })
  const hasGtm = currentWorkspace?.employees.includes('gtm') === true

  return (
    <>
      <div
        className={cn(
          'grid shrink-0 gap-1',
          collapsed && 'justify-items-center',
        )}
      >
        {showWorkspaceSelector ? (
          <div className="pb-2">
            <WorkspaceSelector
              collapsed={collapsed}
              workspaceId={workspaceId}
              workspaces={workspaces}
              currentWorkspace={currentWorkspace}
              onSelect={handleWorkspaceChange}
            />
          </div>
        ) : null}

        {currentWorkspace && hasGtm ? (
          <GtmWorkspaceNav
            workspaceId={currentWorkspace.id}
            collapsible={!isGtmOnlyWorkspace}
            collapsed={collapsed}
          />
        ) : currentWorkspace ? (
          <GtmHireButton
            workspaceId={currentWorkspace.id}
            collapsed={collapsed}
          />
        ) : null}
      </div>

      <SidebarDivider />

      <nav
        className={cn(
          'grid shrink-0 gap-1',
          collapsed && 'justify-items-center',
        )}
        aria-label={t('workspaces')}
      >
        <SidebarNavItem
          icon={Bot}
          label={t('agents')}
          active={pathname.includes('/organization/agents')}
          collapsed={collapsed}
        >
          <Link to="/$lang/organization/agents" params={{ lang }} />
        </SidebarNavItem>
        <SidebarNavItem
          icon={Zap}
          label={t('automations')}
          active={pathname.includes('/organization/automations')}
          collapsed={collapsed}
        >
          <Link to="/$lang/organization/automations" params={{ lang }} />
        </SidebarNavItem>
        <SidebarNavItem
          icon={Settings}
          label={t('settings')}
          active={
            workspaceId.length > 0 &&
            pathname.includes(`/workspace/${workspaceId}/settings`)
          }
          collapsed={collapsed}
        >
          {workspaceId ? (
            <Link
              to="/$lang/workspace/$workspaceId/settings"
              params={{ lang, workspaceId }}
            />
          ) : (
            <button type="button" disabled />
          )}
        </SidebarNavItem>
      </nav>

      <SidebarDivider />

      <SidebarChatList
        collapsed={collapsed}
        workspaceId={workspaceId}
        chats={chats}
        currentChatId={currentChatId}
        respondingChatIds={respondingChatIds}
        draftChatIds={draftChatIds}
        isChatsLoading={isChatsLoading}
        chatsErrorMessage={chatsErrorMessage}
        hasWorkspaceAccess={hasWorkspaceAccess}
        isReadOnly={false}
        onNewChat={handleNewChat}
        onOpenChat={handleOpenChat}
        onDeleteChat={handleDeleteChat}
      />
    </>
  )
}
